// NotificationService.js
import { Auth } from "aws-amplify";
import { API, graphqlOperation } from "aws-amplify";
import { fetchData, postQuery } from "./BackendServices";

// Amplify is configured globally in src/index.js
// DO NOT reconfigure here as it will overwrite the Identity Pool configuration

// GraphQL Subscriptions for real-time notifications
const CREATE_NOTIFICATION_SUBSCRIPTION = `
  subscription OnNotificationCreated($userId: String!) {
    onNotificationCreated(userId: $userId) {
      id
      userId
      title
      message
      type
      isRead
      createdAt
      metadata
    }
  }
`;

const UPDATE_NOTIFICATION_SUBSCRIPTION = `
  subscription OnNotificationUpdated($userId: String!) {
    onNotificationUpdated(userId: $userId) {
      id
      userId
      title
      message
      type
      isRead
      createdAt
      metadata
    }
  }
`;

const DELETE_NOTIFICATION_SUBSCRIPTION = `
  subscription OnNotificationDeleted($userId: String!) {
    onNotificationDeleted(userId: $userId) {
      id
      userId
      title
      message
      type
      isRead
      createdAt
      metadata
    }
  }
`;

class NotificationService {
  constructor() {
    this.subscriptions = new Map();
    this.listeners = new Map();
    this.currentUserId = null;
    this.notifications = [];
    this.unreadCount = 0;
    this.pollingInterval = null;
    this.subscriptionsActive = false;
  }

  // Initialize the service with current user
  async initialize() {
    try {
      // Check if user is authenticated first
      const user = await Auth.currentAuthenticatedUser().catch(() => null);
      
      if (!user) {
        console.log("⚠️ NotificationService: User not authenticated, skipping initialization");
        return false;
      }
      
      this.currentUserId = user.attributes.sub;

      // Load notifications with error handling
      await this.loadNotifications().catch((error) => {
        console.error("⚠️ Failed to load notifications:", error);
        // Don't fail initialization if notifications fail to load
      });
      
      this.setupSubscriptions();

      return true;
    } catch (error) {
      console.error("❌ Failed to initialize NotificationService:", error);
      return false;
    }
  }

  // Setup real-time subscriptions
  setupSubscriptions() {
    if (!this.currentUserId) {
      console.log(
        "⚠️ NotificationService: No current user ID, skipping subscriptions"
      );
      return;
    }

    console.log(
      "🔔 NotificationService: Setting up subscriptions for user:",
      this.currentUserId
    );

    try {
      // Subscribe to new notifications
      const createSub = API.graphql(
        graphqlOperation(CREATE_NOTIFICATION_SUBSCRIPTION, {
          userId: this.currentUserId,
        })
      ).subscribe({
        next: ({ value }) => {
          const notification = value.data.onNotificationCreated;
          this.addNotification(notification);
          this.notifyListeners("notificationCreated", notification);
        },
        error: (err) => {
          console.warn("⚠️ Create subscription error (non-critical):", err.message || err);
          // Subscriptions are optional - don't break the app
        },
      });

      // Subscribe to notification updates
      const updateSub = API.graphql(
        graphqlOperation(UPDATE_NOTIFICATION_SUBSCRIPTION, {
          userId: this.currentUserId,
        })
      ).subscribe({
        next: ({ value }) => {
          const notification = value.data.onNotificationUpdated;
          console.log(
            "📝 NotificationService: Notification updated:",
            notification
          );
          this.updateNotification(notification);
          this.notifyListeners("notificationUpdated", notification);
        },
        error: (err) => {
          console.warn("⚠️ Update subscription error (non-critical):", err.message || err);
          // Don't fail completely, just log the error
        },
      });

      // Subscribe to notification deletions
      const deleteSub = API.graphql(
        graphqlOperation(DELETE_NOTIFICATION_SUBSCRIPTION, {
          userId: this.currentUserId,
        })
      ).subscribe({
        next: ({ value }) => {
          const notification = value.data.onNotificationDeleted;
          console.log(
            "🗑️ NotificationService: Notification deleted:",
            notification
          );
          this.removeNotification(notification.id);
          this.notifyListeners("notificationDeleted", notification);
        },
        error: (err) => {
          console.warn("⚠️ Delete subscription error (non-critical):", err.message || err);
          // Don't fail completely, just log the error
        },
      });

      this.subscriptions.set("create", createSub);
      this.subscriptions.set("update", updateSub);
      this.subscriptions.set("delete", deleteSub);

      this.subscriptionsActive = true;
      console.log(
        "✅ NotificationService: All subscriptions set up successfully"
      );
    } catch (error) {
      console.warn(
        "⚠️ NotificationService: Subscriptions not available (this is normal for local development):",
        error.message || error
      );
      console.log("🔄 NotificationService: Using polling mechanism instead");
      this.setupPolling();
    }
  }

  // Fallback polling mechanism when subscriptions fail
  setupPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    console.log("🔄 NotificationService: Setting up polling every 30 seconds");
    this.pollingInterval = setInterval(async () => {
      try {
        console.log("📊 NotificationService: Polling for new notifications");
        const currentCount = this.notifications.length;
        await this.loadNotifications(50, null, {});

        // Check if new notifications were added
        if (this.notifications.length > currentCount) {
          console.log(
            "📨 NotificationService: New notifications detected via polling"
          );
        }
      } catch (error) {
        console.error("❌ Polling error:", error);
      }
    }, 30000); // Poll every 30 seconds
  }

  // Load notifications for current user
  async loadNotifications(limit = 50, nextToken = null, filters = {}) {
    try {
      const response = await fetchData({
        target: "listUserNotifications",
        userId: this.currentUserId,
        userOrder: "createdAt DESC",
        rowStart: nextToken ? parseInt(nextToken) : 0,
        pageSize: limit,
        ...filters,
      });

      if (response.items) {
        if (nextToken) {
          // Append to existing notifications for pagination
          this.notifications = [...this.notifications, ...response.items];
        } else {
          // Replace notifications for fresh load
          this.notifications = response.items;
        }
        this.calculateUnreadCount();
        this.notifyListeners("notificationsLoaded", {
          notifications: this.notifications,
          nextToken: response.nextToken,
          totalCount: response.totalCount,
        });
      }

      return response;
    } catch (error) {
      console.error("Failed to load notifications:", error);
      throw error;
    }
  }

  // Create a new notification (typically called from backend)
  async createNotification(notificationData) {
    try {
      const response = await fetchData({
        target: "createNotification",
        input: {
          userId: notificationData.userId,
          title: notificationData.title,
          message: notificationData.message,
          type: notificationData.type || "info",
          metadata: notificationData.metadata
            ? JSON.stringify(notificationData.metadata)
            : null,
        },
      });

      return response;
    } catch (error) {
      console.error("Failed to create notification:", error);
      throw error;
    }
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const response = await fetchData({
        target: "markNotificationAsRead",
        id: notificationId,
      });

      // Update local state
      const notification = this.notifications.find(
        (n) => n.id === notificationId
      );
      if (notification) {
        notification.isRead = true;
        this.calculateUnreadCount();
        this.notifyListeners("notificationRead", notification);
      }

      return response;
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      throw error;
    }
  }

  // Mark all notifications as read
  async markAllAsRead() {
    const unreadNotifications = this.notifications.filter((n) => !n.isRead);
    const promises = unreadNotifications.map((n) => this.markAsRead(n.id));

    try {
      await Promise.all(promises);
      this.calculateUnreadCount();
      this.notifyListeners("allNotificationsRead", null);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      throw error;
    }
  }

  // Delete a notification
  async deleteNotification(notificationId) {
    try {
      const response = await postQuery({
        target: "deleteNotification",
        id: notificationId,
      });

      return response;
    } catch (error) {
      console.error("Failed to delete notification:", error);
      throw error;
    }
  }

  // Force refresh notifications (useful after operations like test notifications)
  async forceRefresh() {
    console.log("🔄 NotificationService: Force refreshing notifications");
    try {
      await this.loadNotifications();
      console.log(
        "✅ NotificationService: Force refresh completed successfully"
      );
      return true;
    } catch (error) {
      console.error("❌ NotificationService: Force refresh failed:", error);
      return false;
    }
  }

  // Get notifications with filtering
  async getFilteredNotifications(filters) {
    return this.loadNotifications(50, null, filters);
  }

  // Local state management methods
  addNotification(notification) {
    console.log("➕ NotificationService: Adding notification:", notification);
    // Add to beginning of array (most recent first)
    this.notifications.unshift(notification);
    console.log(
      "📊 NotificationService: Total notifications now:",
      this.notifications.length
    );
    this.calculateUnreadCount();
  }

  updateNotification(updatedNotification) {
    console.log(
      "📝 NotificationService: Updating notification:",
      updatedNotification
    );
    const index = this.notifications.findIndex(
      (n) => n.id === updatedNotification.id
    );
    if (index !== -1) {
      this.notifications[index] = updatedNotification;
      console.log(
        "✅ NotificationService: Notification updated at index:",
        index
      );
      this.calculateUnreadCount();
    } else {
      console.log(
        "⚠️ NotificationService: Notification not found for update:",
        updatedNotification.id
      );
    }
  }

  removeNotification(notificationId) {
    console.log(
      "🗑️ NotificationService: Removing notification:",
      notificationId
    );
    const beforeCount = this.notifications.length;
    this.notifications = this.notifications.filter(
      (n) => n.id !== notificationId
    );
    console.log(
      "📊 NotificationService: Removed notification, count:",
      beforeCount,
      "->",
      this.notifications.length
    );
    this.calculateUnreadCount();
  }

  calculateUnreadCount() {
    const previousCount = this.unreadCount;

    // Debug: Log the isRead values we're seeing
    const isReadValues = this.notifications.map((n) => ({
      id: n.id,
      isRead: n.isRead,
      type: typeof n.isRead,
    }));
    console.log("🔍 NotificationService: isRead values debug:", isReadValues);

    // Handle both boolean and integer isRead values
    this.unreadCount = this.notifications.filter((n) => {
      // Handle TINYINT(2) from database (0/1) or boolean (true/false)
      const isRead = n.isRead === true || n.isRead === 1 || n.isRead === "1";
      return !isRead;
    }).length;

    console.log(
      "🔢 NotificationService: Unread count changed:",
      previousCount,
      "->",
      this.unreadCount
    );
    console.log(
      "📋 NotificationService: Unread notifications:",
      this.notifications.filter((n) => {
        const isRead = n.isRead === true || n.isRead === 1 || n.isRead === "1";
        return !isRead;
      })
    );
    this.notifyListeners("unreadCountChanged", this.unreadCount);
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return cleanup function
    return () => {
      this.removeEventListener(event, callback);
    };
  }

  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  notifyListeners(event, data) {
    console.log(
      `📡 NotificationService: Notifying listeners for event "${event}":`,
      data
    );
    if (this.listeners.has(event)) {
      const listeners = this.listeners.get(event);
      console.log(
        `👂 NotificationService: Found ${listeners.size} listeners for "${event}"`
      );
      this.listeners.get(event).forEach((callback, index) => {
        try {
          console.log(
            `🔄 NotificationService: Calling listener ${index} for "${event}"`
          );
          callback(data);
        } catch (error) {
          console.error(`❌ Error in ${event} listener ${index}:`, error);
        }
      });
    } else {
      console.log(
        `⚠️ NotificationService: No listeners found for event "${event}"`
      );
    }
  }

  // Getters for current state
  getNotifications() {
    return [...this.notifications];
  }

  getUnreadCount() {
    return this.unreadCount;
  }

  getCurrentUserId() {
    return this.currentUserId;
  }

  // Cleanup subscriptions
  cleanup() {
    // Clean up subscriptions
    this.subscriptions.forEach((subscription) => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    });
    this.subscriptions.clear();
    this.listeners.clear();

    // Clean up polling
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    this.subscriptionsActive = false;
    console.log("🧹 NotificationService: Cleanup completed");
  }

  // Utility methods for notification types
  static NotificationTypes = {
    INFO: "info",
    SUCCESS: "success",
    WARNING: "warning",
    ERROR: "error",
    OFFER_RECEIVED: "offer_received",
    OFFER_ACCEPTED: "offer_accepted",
    OFFER_REJECTED: "offer_rejected",
    REQUIREMENT_MATCHED: "requirement_matched",
    PAYMENT_DUE: "payment_due",
    SYSTEM_UPDATE: "system_update",
  };

  // Helper method to create notification with proper structure
  static createNotificationData(
    userId,
    title,
    message,
    type = "info",
    metadata = null
  ) {
    return {
      userId,
      title,
      message,
      type,
      metadata,
    };
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
